import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Settings, Clock, Hash } from 'lucide-react';
import { useApp } from '../context/AppContext';

const Home = memo(function Home() {
  const navigate = useNavigate();
  const { streak, getStatsSummary, settings, updateSettings } = useApp();
  const stats = getStatsSummary;
  
  const isTimedMode = settings.sessionMode === 'timed';
  
  const handleStartSession = (type) => {
    const sessionMode = settings.sessionMode || 'timed';
    if (type === 'intervals') {
      navigate(`/practice/intervals?mode=${sessionMode}`);
    } else {
      navigate(`/practice?mode=${sessionMode}`);
    }
  };

  const handleToggleSessionMode = () => {
    updateSettings({ sessionMode: isTimedMode ? 'count' : 'timed' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="min-h-screen bg-cream-100 paper-texture"
    >
      <div className="max-w-lg mx-auto px-6 py-8 pb-24">
        {/* Header */}
        <header className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl text-cream-900 tracking-tight">
              Glissando
            </h1>
            <p className="font-sans text-sm text-cream-800 mt-1">
              Play music with flow, not friction.
            </p>
          </div>
          <button 
            onClick={() => navigate('/settings')}
            className="icon-btn w-12 h-12 rounded-full flex items-center justify-center transition-colors"
            style={{ 
              backgroundColor: 'rgba(107, 127, 140, 0.1)',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.1)'}
            onMouseDown={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.3)'}
            onMouseUp={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.2)'}
            aria-label="Settings"
            data-testid="settings-btn"
          >
            <Settings className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
          </button>
        </header>

        {/* Today's Practice Section */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl text-cream-900">Today's Practice</h2>
          <button
            onClick={handleToggleSessionMode}
            className="flex items-center gap-1.5 text-cream-800 hover:text-cream-900 transition-colors"
            data-testid="session-mode-toggle"
          >
            {isTimedMode ? (
              <>
                <Clock className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm font-sans">60 seconds</span>
              </>
            ) : (
              <>
                <Hash className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm font-sans">20 cards</span>
              </>
            )}
          </button>
        </div>
        
        <div className="space-y-4">
          {/* Sight Reading Card - Large */}
          <div
            className="bg-white border border-wood-200 shadow-sm rounded-2xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => handleStartSession('sight-reading')}
            data-testid="sight-reading-card"
          >
            <div className="flex items-stretch">
              {/* Illustration */}
              <div className="w-32 flex-shrink-0 flex items-center justify-center py-4 pl-3">
                <img
                  src="/general-session-illustration.png?v=2"
                  alt="Sight Reading"
                  className="w-full h-auto max-h-28 object-contain"
                />
              </div>
              {/* Content */}
              <div className="flex-1 p-4 flex flex-col justify-center">
                <h3 className="font-serif text-base text-cream-900 mb-1">Sight Reading</h3>
                <p className="font-sans text-sm text-cream-800 mb-3">
                  Train your eye-to-hand connection, one note at a time.
                </p>
                <span className="flex items-center gap-1 text-coral-500 font-sans font-medium text-sm">
                  Begin Session
                  <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </div>

          {/* Two Column Layout for Intervals and Chords */}
          <div className="grid grid-cols-2 gap-4">
            {/* Intervals Card */}
            <div
              className="bg-white border border-wood-200 shadow-sm rounded-2xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleStartSession('intervals')}
              data-testid="intervals-card"
            >
              <div className="p-4 flex flex-col items-center text-center">
                {/* Illustration */}
                <div className="w-20 h-20 mb-3 flex items-center justify-center">
                  <img
                    src="/intervals-illustration.png?v=20260310"
                    alt="Intervals"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="font-serif text-base text-cream-900 mb-1">Intervals</h3>
                <p className="font-sans text-sm text-cream-800 mb-3">
                  Recognize the distance between notes by sight.
                </p>
                <span className="flex items-center gap-1 text-coral-500 font-sans font-medium text-sm">
                  Begin
                  <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </div>

            {/* Chords Card - Coming Soon */}
            <div 
              className="bg-white border border-wood-200 shadow-sm rounded-2xl overflow-hidden opacity-75"
              data-testid="chords-card"
            >
              <div className="p-4 flex flex-col items-center text-center">
                {/* Illustration */}
                <div className="w-20 h-20 mb-3 flex items-center justify-center">
                  <img 
                    src="/chords-illustration.png?v=20260310" 
                    alt="Chords" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="font-serif text-base text-cream-900 mb-1">Chords</h3>
                <p className="font-sans text-sm text-cream-800 mb-3">
                  Recognize chord shapes on the staff and keyboard.
                </p>
                <span className="inline-block px-3 py-1 bg-wood-100 text-wood-700 font-sans font-medium text-xs rounded-full">
                  COMING SOON
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="flex justify-center my-8">
          <div className="w-16 h-0.5 bg-wood-200 rounded-full"></div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-3 gap-4">
          {/* Streak */}
          <div className="text-center">
            <div className="flex justify-center mb-2">
              <img src="/icon-streak.svg" alt="Streak" className="w-6 h-6" />
            </div>
            <p className="font-serif text-2xl text-cream-900">
              {streak.currentCount} <span className="text-base font-serif text-cream-900">Days</span>
            </p>
            <p className="text-xs text-cream-900 font-sans font-semibold uppercase tracking-wider mt-1">Streak</p>
          </div>
          
          {/* Accuracy */}
          <div className="text-center">
            <div className="flex justify-center mb-2">
              <img src="/icon-accuracy.svg" alt="Accuracy" className="w-6 h-6" />
            </div>
            <p className="font-serif text-2xl text-cream-900">
              {stats.accuracy}<span className="text-base">%</span>
            </p>
            <p className="text-xs text-cream-900 font-sans font-semibold uppercase tracking-wider mt-1">Accuracy</p>
          </div>
          
          {/* Notes Mastered */}
          <div className="text-center">
            <div className="flex justify-center mb-2">
              <img src="/icon-mastery.svg" alt="Mastery" className="w-6 h-6" />
            </div>
            <p className="font-serif text-2xl text-cream-900">
              {stats.masteredCount}<span className="text-base text-cream-800">/{stats.totalNotes}</span>
            </p>
            <p className="text-xs text-cream-900 font-sans font-semibold uppercase tracking-wider mt-1">Notes Mastered</p>
          </div>
        </div>

        {/* View Progress Link */}
        <button
          className="w-full mt-8 flex items-center justify-center gap-1 text-cream-800 hover:text-cream-900 font-sans text-sm transition-colors"
          onClick={() => navigate('/progress')}
          data-testid="view-progress-btn"
        >
          View all progress
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
});

export default Home;
